import { useQuery, keepPreviousData } from '@tanstack/react-query'
import * as api from '@/api/root'
import type { ActivityFilters, DocumentFilters, SignatureFilters, UserFilters } from '@/types/api'

export function useDashboard() {
  return useQuery({
    queryKey: ['dashboard'],
    queryFn: api.getDashboard,
    refetchInterval: 30_000,
  })
}

// pollIntervalMs is configurable per spec section 11 ("if polling is used,
// make the interval configurable") — Activity page exposes a picker that
// changes this value.
export function useActivity(filters: ActivityFilters, pollIntervalMs: number | false = false) {
  return useQuery({
    queryKey: ['activity', filters],
    queryFn: () => api.listActivity(filters),
    placeholderData: keepPreviousData,
    refetchInterval: pollIntervalMs,
  })
}

export function useActivityDetail(id: string | null) {
  return useQuery({
    queryKey: ['activity', 'detail', id],
    queryFn: () => api.getActivityDetail(id as string),
    enabled: !!id,
  })
}

export function useOrganizations(params: {
  search?: string
  status?: string
  page?: number
  pageSize?: number
}) {
  return useQuery({
    queryKey: ['organizations', params],
    queryFn: () => api.listOrganizations(params),
    placeholderData: keepPreviousData,
  })
}

export function useOrganization(id: string | undefined) {
  return useQuery({
    queryKey: ['organization', id],
    queryFn: () => api.getOrganization(id as string),
    enabled: !!id,
  })
}

export function useOrganizationUsers(id: string | undefined, userType?: string, page = 1) {
  return useQuery({
    queryKey: ['organization', id, 'users', userType, page],
    queryFn: () => api.listOrganizationUsers(id as string, userType, page),
    enabled: !!id,
    placeholderData: keepPreviousData,
  })
}

export function useUsers(params: UserFilters) {
  return useQuery({
    queryKey: ['users', params],
    queryFn: () => api.listUsers(params),
    placeholderData: keepPreviousData,
  })
}

export function useUser(id: string | undefined) {
  return useQuery({
    queryKey: ['user', id],
    queryFn: () => api.getUser(id as string),
    enabled: !!id,
  })
}

export function useSecuritySummary() {
  return useQuery({
    queryKey: ['security-summary'],
    queryFn: api.getSecuritySummary,
    refetchInterval: 30_000,
  })
}

export function useAnalytics(range: '24h' | '7d' | '30d' | '90d' | 'all') {
  return useQuery({
    queryKey: ['analytics', range],
    queryFn: () => api.getAnalytics(range),
  })
}

export function useSystemHealth() {
  return useQuery({
    queryKey: ['system-health'],
    queryFn: api.getSystemHealth,
    refetchInterval: 15_000,
  })
}

export function useDocuments(params: DocumentFilters) {
  return useQuery({
    queryKey: ['documents', params],
    queryFn: () => api.listDocuments(params),
    placeholderData: keepPreviousData,
  })
}

export function useDocument(id: string | undefined) {
  return useQuery({
    queryKey: ['document', id],
    queryFn: () => api.getDocument(id as string),
    enabled: !!id,
  })
}

export function useSignatures(params: SignatureFilters) {
  return useQuery({
    queryKey: ['signatures', params],
    queryFn: () => api.listSignatures(params),
    placeholderData: keepPreviousData,
  })
}

export function useSignature(id: string | undefined) {
  return useQuery({
    queryKey: ['signature', id],
    queryFn: () => api.getSignature(id as string),
    enabled: !!id,
  })
}

export function useGlobalSearch(q: string) {
  return useQuery({
    queryKey: ['search', q],
    queryFn: () => api.globalSearch(q),
    enabled: q.trim().length > 0,
    placeholderData: keepPreviousData,
  })
}
